// src/App.tsx - Neuro Core Shell
// This is the core application shell with only kernel routes.
// All feature pages are loaded via plugins through PluginPageHost.

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import TopNav from './components/TopNav';
import { LoadingFallback } from './components/LoadingFallback';
import RequireAuth from './components/RequireAuth';
import { AppShell } from './components/layout';
import { CorePageWrapper } from './components/CorePageWrapper';

// Eagerly loaded pages (critical path)
import LoginPage from './pages/LoginPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

// Plugin Management Pages (core)
const PluginListPage = lazy(() => import('./pages/settings/plugins/PluginListPage'));
const PluginDetailPage = lazy(() => import('./pages/settings/plugins/PluginDetailPage'));
const PluginSettingsPage = lazy(() => import('./pages/settings/plugins/PluginSettingsPage'));
const PluginLogsPage = lazy(() => import('./pages/settings/plugins/PluginLogsPage'));
const PluginHealthPage = lazy(() => import('./pages/settings/plugins/PluginHealthPage'));
const PluginInstallPage = lazy(() => import('./pages/settings/plugins/PluginInstallPage'));

// Workflow Pages (core) — previously mis-attributed to a plugin shim.
// These are first-party core pages that use core stores directly.
const WorkflowsWorkspace = lazy(() => import('./pages/workflows/workspace/WorkflowsWorkspace'));
const WorkflowBuilderPage = lazy(() => import('./pages/workflows/builder/WorkflowBuilderPage'));
const WorkflowRunsRedirect = lazy(() => import('./pages/workflows/runs/WorkflowRunsRedirect'));

// Runner Pages (Core)
const RunnersPage = lazy(() => import('./pages/runners/RunnersPage'));
const RunnerDetailPage = lazy(() => import('./pages/runners/RunnerDetailPage'));

// Agent Governance Pages (Core)
const AgentCockpitPage = lazy(() => import('./pages/agents/AgentCockpitPage'));

// Graph Pages (Core)
const GraphExplorerPage = lazy(() => import('./pages/graph/GraphExplorerPage'));
const SearchConsolePage = lazy(() => import('./pages/search/SearchConsolePage'));
const ApprovalInboxPage = lazy(() => import('./pages/approvals/ApprovalInboxPage'));
const ArtifactExplorerPage = lazy(() => import('./pages/artifacts/ArtifactExplorerPage'));
const WorkflowRunsWorkspace = lazy(() => import('./pages/workflows/runs/WorkflowRunsWorkspace'));
const ExecutionTraceViewerPage = lazy(() => import('./pages/traces/ExecutionTraceViewerPage'));

// DevTools Pages (core)
const SystemHealthPage = lazy(() => import('./pages/system-health/SystemHealthPage'));
const EmbedHarnessPage = lazy(() => import('./pages/devtools/EmbedHarnessPage'));

// Plugin Page Host for dynamic plugin routes (catches all unmatched paths)
import { PluginPageHost } from './plugins/extension-points/PluginPageHost';
import { PluginLoader } from './plugins/PluginLoader';

// Plugin Modal Host — renders plugin-initiated modals above all content
import PluginModalHost from './components/PluginModalHost';

// Assistant Overlay Host — system-wide contextual assistant popout,
// reachable from every page's toolbar / command palette / Ctrl+. (Cmd+. on Mac).
import { AssistantOverlayHost } from './components/assistant/AssistantOverlayHost';

// InteractiveSessionProvider — fallback modal for actions without
// a registered plugin handler (generic textarea-based interactive editing).
import { InteractiveSessionProvider } from './plugins/InteractiveSessionProvider';

// Notification toasts — must render inside PluginLoader's ThemeProvider, which
// sets the --nc-color-* tokens on its wrapper div. Outside it the toasts get no
// background (see-through) and no text/border colors.
import { NotificationToastContainer } from './components/notifications';

export default function App() {
  return (
    <PluginLoader>
      <AppShell navbar={<TopNav />}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/invite/:token" element={<InviteAcceptPage />} />
            
            {/* Plugin Management Routes (Core) */}
            <Route path="/plugins" element={<RequireAuth><CorePageWrapper pageId="/plugins"><PluginListPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><CorePageWrapper pageId="/settings"><SettingsPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/settings/plugins" element={<RequireAuth><CorePageWrapper pageId="/settings/plugins"><PluginListPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/settings/plugins/install" element={<RequireAuth><CorePageWrapper pageId="/settings/plugins/install"><PluginInstallPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/settings/plugins/:pluginId" element={<RequireAuth><CorePageWrapper pageId="/settings/plugins/:pluginId" entityType="plugin" entityId=""><PluginDetailPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/settings/plugins/:pluginId/settings" element={<RequireAuth><CorePageWrapper pageId="/settings/plugins/:pluginId/settings" entityType="plugin" entityId=""><PluginSettingsPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/settings/plugins/:pluginId/logs" element={<RequireAuth><CorePageWrapper pageId="/settings/plugins/:pluginId/logs" entityType="plugin" entityId=""><PluginLogsPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/settings/plugins/:pluginId/health" element={<RequireAuth><CorePageWrapper pageId="/settings/plugins/:pluginId/health" entityType="plugin" entityId=""><PluginHealthPage /></CorePageWrapper></RequireAuth>} />
            
            {/* Workflow Routes (Core) */}
            <Route path="/workflows" element={<RequireAuth><CorePageWrapper pageId="/workflows" entityType="workflow"><WorkflowsWorkspace /></CorePageWrapper></RequireAuth>} />
            <Route path="/workflows/templates" element={<RequireAuth><CorePageWrapper pageId="/workflows" entityType="workflow"><WorkflowsWorkspace /></CorePageWrapper></RequireAuth>} />
            <Route path="/workflows/new" element={<RequireAuth><CorePageWrapper pageId="/workflows" entityType="workflow"><WorkflowsWorkspace /></CorePageWrapper></RequireAuth>} />
            <Route path="/workflows/draft" element={<RequireAuth><CorePageWrapper pageId="/workflows/draft" entityType="workflow"><WorkflowBuilderPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/workflows/ai" element={<Navigate to="/workflows/new?mode=ai" replace />} />
            <Route path="/workflows/analytics/*" element={<Navigate to="/workflows" replace />} />
            <Route path="/workflows/:workflowId" element={<RequireAuth><CorePageWrapper pageId="/workflows" entityType="workflow"><WorkflowsWorkspace /></CorePageWrapper></RequireAuth>} />
            <Route path="/workflows/:workflowId/edit" element={<RequireAuth><CorePageWrapper pageId="/workflows/:id/edit" entityType="workflow"><WorkflowBuilderPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/workflows/:id/runs" element={<RequireAuth><WorkflowRunsRedirect /></RequireAuth>} />
            
            {/* Runner Routes (Core) */}
            <Route path="/runners" element={<RequireAuth><CorePageWrapper pageId="/runners" entityType="runner"><RunnersPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/runners/:runnerId" element={<RequireAuth><CorePageWrapper pageId="/runners/:runnerId" entityType="runner" entityId=""><RunnerDetailPage /></CorePageWrapper></RequireAuth>} />

            {/* Agent Governance Routes (Core) */}
            <Route path="/agents/cockpit" element={<RequireAuth><CorePageWrapper pageId="/agents/cockpit" entityType="agent"><AgentCockpitPage /></CorePageWrapper></RequireAuth>} />

            {/* Graph Routes (Core) */}
            <Route path="/graph" element={<RequireAuth><CorePageWrapper pageId="/graph"><GraphExplorerPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/search" element={<RequireAuth><CorePageWrapper pageId="/search"><SearchConsolePage /></CorePageWrapper></RequireAuth>} />
            <Route path="/approvals" element={<RequireAuth><CorePageWrapper pageId="/approvals"><ApprovalInboxPage /></CorePageWrapper></RequireAuth>} />
            <Route path="/artifacts" element={<RequireAuth><CorePageWrapper pageId="/artifacts" entityType="artifact"><ArtifactExplorerPage /></CorePageWrapper></RequireAuth>} />
            {/* One route for the list and an open run, so opening a run keeps the workspace mounted. */}
            <Route path="/workflow-runs/:runId?" element={<RequireAuth><CorePageWrapper pageId="/workflow-runs" entityType="workflow"><WorkflowRunsWorkspace /></CorePageWrapper></RequireAuth>} />
            <Route path="/execution-traces" element={<RequireAuth><CorePageWrapper pageId="/execution-traces"><ExecutionTraceViewerPage /></CorePageWrapper></RequireAuth>} />

            {/* DevTools Routes (Core) */}
            <Route path="/system-health" element={<RequireAuth><CorePageWrapper pageId="/system-health"><SystemHealthPage /></CorePageWrapper></RequireAuth>} />
            {/* The old Observability page lived here; System health replaced it. */}
            <Route path="/devtools" element={<Navigate to="/system-health" replace />} />
            <Route path="/devtools/observability" element={<Navigate to="/system-health" replace />} />
            <Route path="/devtools/embed-harness" element={<RequireAuth><CorePageWrapper pageId="/devtools/embed-harness"><EmbedHarnessPage /></CorePageWrapper></RequireAuth>} />
            
            {/* Catch-all route — delegates to plugin system */}
            <Route 
              path="*" 
              element={
                <RequireAuth>
                  <PluginPageHost />
                </RequireAuth>
              } 
            />
          </Routes>
        </Suspense>
      </AppShell>
      <PluginModalHost />
      <AssistantOverlayHost />
      <InteractiveSessionProvider children={null} />
      <NotificationToastContainer />
    </PluginLoader>
  );
}
